import { Tabs, TabsProps, Tag, Upload } from 'antd'
import React, { SyntheticEvent, useRef, useState } from 'react'
import style from 'assets/css/Form.module.css'
import { useStateContext } from 'context/ContextProvider'
import { User } from 'types'
import { useNavigate } from 'react-router-dom'
import axiosClient from 'axios-client'
import { ReactComponent as IcoAdd } from 'assets/icons/add.svg'

const Profile: React.FC = () => {
  const { user, setUser } = useStateContext()

  const navigate = useNavigate()

  // User details
  const [authUser, setAuthUser] = useState<User | null>(user)
  const onSubmitUser = (ev: SyntheticEvent) => {
    ev.preventDefault()

    axiosClient
      .put(`/users/${authUser?.id}`, authUser)
      .then(() => {
        setUser(authUser)
      })
  }

  // Password change
  const currentPW = useRef<HTMLInputElement>(null)
  const newPW = useRef<HTMLInputElement>(null)
  const confirmPW = useRef<HTMLInputElement>(null)

  const onSubmitPassword = (ev: SyntheticEvent) => {
    ev.preventDefault()

    axiosClient
      .put(`/users/changePassword`, {
        current: currentPW.current?.value,
        password: newPW.current?.value,
        password_confirmation: confirmPW.current?.value,
      })
  }

  // Client details
  const [client, setClient] = useState(authUser?.client)
  const onSubmitCompany = (ev: SyntheticEvent) => {
    ev.preventDefault()
    if (!client) return

    axiosClient.put(`/clients/${client.id}`, client)
  }

  if (!authUser) return <></>
  if (!client) return <></>

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Account Details',
      children: (
        <>
          <form
            className={`${style.form}`}
            onSubmit={onSubmitUser}
            style={{ width: '50%', margin: 'auto' }}
          >
            <div className={`${style.container}`}>
              <div className={`${style.input_container}`}>
                <label
                  className={`${style.label}`}
                  htmlFor="first"
                >
                  First name *
                </label>
                <input
                  className={`${style.input}`}
                  type="text"
                  name="first"
                  id="first"
                  value={authUser.firstname}
                  onChange={ev => setAuthUser({ ...authUser, firstname: ev.target.value })}
                  required
                />
              </div>
              <div className={`${style.input_container}`}>
                <label
                  className={`${style.label}`}
                  htmlFor="last"
                >
                  Last name *
                </label>
                <input
                  className={`${style.input}`}
                  type="text"
                  name="last"
                  id="last"
                  value={authUser.lastname}
                  onChange={ev => setAuthUser({ ...authUser, lastname: ev.target.value })}
                />
              </div>

              <div className={`${style.input_container} ${style.span2}`}>
                <label
                  className={`${style.label}`}
                  htmlFor="email"
                >
                  Email *
                </label>
                <input
                  className={`${style.input}`}
                  type="email"
                  name="email"
                  id="email"
                  value={authUser.email}
                  onChange={ev => setAuthUser({ ...authUser, email: ev.target.value })}
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              className={`${style.button} ${style.submit}`}
              style={{ width: '30%' }}
            >
              Save
            </button>
            <button
              type="reset"
              className={`${style.button} ${style.cancel}`}
              style={{ width: '30%' }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </form>
        </>
      ),
    },
    {
      key: '2',
      label: 'Change Password',
      children: (
        <>
          <form
            className={`${style.form}`}
            onSubmit={onSubmitPassword}
            style={{ width: '50%', margin: 'auto' }}
          >
            <div className={`${style.container}`}>
              <div className={`${style.input_container} ${style.span2}`}>
                <label
                  className={`${style.label}`}
                  htmlFor="current"
                >
                  Current Password *
                </label>
                <input
                  className={`${style.input}`}
                  type="password"
                  name="current"
                  id="current"
                  ref={currentPW}
                  required
                />
              </div>
              <div className={`${style.input_container} ${style.span2}`}>
                <label
                  className={`${style.label}`}
                  htmlFor="newPass"
                >
                  New Password *
                </label>
                <input
                  className={`${style.input}`}
                  type="password"
                  name="newPass"
                  id="newPass"
                  ref={newPW}
                  required
                />
              </div>

              <div className={`${style.input_container} ${style.span2}`}>
                <label
                  className={`${style.label}`}
                  htmlFor="confirmPass"
                >
                  Confirm Password *
                </label>
                <input
                  className={`${style.input}`}
                  type="password"
                  name="confirmPass"
                  id="confirmPass"
                  ref={confirmPW}
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              className={`${style.button} ${style.submit}`}
              style={{ width: '30%' }}
            >
              Save
            </button>
            <button
              type="reset"
              className={`${style.button} ${style.cancel}`}
              style={{ width: '30%' }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </form>
        </>
      ),
    },
    {
      key: '3',
      label: 'Company Details',
      children: (
        <>
          <form
            className={`${style.form}`}
            onSubmit={onSubmitCompany}
          >
            <div
              style={{
                display: 'grid',
                width: '100%',
                gap: '0 15px',
                grid: 'auto-flow/ 1fr 4fr',
                marginTop: '20px',
              }}
            >
              <div style={{ width: '150px' }}>Client information</div>
              <div>Contact information</div>

              <div className={`${style.imageContainer}`}>
                <Upload
                  name="logo"
                  listType="picture-card"
                  showUploadList={false}
                  className={`${style.image}`}
                >
                  <div className={`${style.initial}`}>MM</div>
                </Upload>
                <button className={`${style.upload}`}>
                  <IcoAdd className={`${style.plus}`} />
                  Upload Logo
                </button>
              </div>

              <div className={`${style.container}`}>
                <div className={`${style.input_container}`}>
                  <label
                    className={`${style.label}`}
                    htmlFor="name"
                  >
                    Client Name *
                  </label>
                  <input
                    className={`${style.input}`}
                    type="text"
                    name="name"
                    id="name"
                    value={client.name || ''}
                    onChange={ev => setClient({ ...client, name: ev.target.value })}
                    required
                  />
                </div>

                <div className={`${style.input_container}`}>
                  <label
                    className={`${style.label}`}
                    htmlFor="website"
                  >
                    Website
                  </label>
                  <input
                    className={`${style.input}`}
                    type="url"
                    name="website"
                    id="website"
                    value={client.website || ''}
                    onChange={ev => setClient({ ...client, website: ev.target.value })}
                  />
                </div>

                <div className={`${style.input_container}`}>
                  <label
                    className={`${style.label}`}
                    htmlFor="ice"
                  >
                    ICE
                  </label>
                  <input
                    className={`${style.input}`}
                    type="text"
                    name="ice"
                    id="ice"
                    value={client.ice || ''}
                    onChange={ev => setClient({ ...client, ice: ev.target.value })}
                  />
                </div>

                <div className={`${style.input_container}`}>
                  <label
                    className={`${style.label}`}
                    htmlFor="email"
                  >
                    Email *
                  </label>
                  <input
                    className={`${style.input}`}
                    type="email"
                    name="email"
                    id="email"
                    value={client.email || ''}
                    onChange={ev => setClient({ ...client, email: ev.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', grid: 'auto / 1fr 1fr', gap: 10 }}>
                  <div className={`${style.input_container}`}>
                    <label
                      className={`${style.label}`}
                      htmlFor="subscription"
                    >
                      Subscription *
                    </label>
                    <Tag>
                      {client.subscription} until {client.subs_date}
                    </Tag>
                  </div>
                </div>

                <div className={`${style.input_container}`}>
                  <label
                    className={`${style.label}`}
                    htmlFor="phone"
                  >
                    Phone number
                  </label>
                  <input
                    className={`${style.input}`}
                    type="tel"
                    name="phone"
                    id="phone"
                    value={client.phone || ''}
                    onChange={ev => setClient({ ...client, phone: ev.target.value })}
                  />
                </div>

                <div className={`${style.input_container}`}>
                  <label
                    className={`${style.label}`}
                    htmlFor="contract"
                  >
                    Contract *
                  </label>

                  <Tag> {client.contract ? client.contract : 'No contract'} </Tag>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className={`${style.button} ${style.submit}`}
            >
              Save
            </button>
            <button
              type="reset"
              className={`${style.button} ${style.cancel}`}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </form>
        </>
      ),
    },
  ]

  return (
    <>
      <button onClick={() => navigate(-1)}> back</button>
      <Tabs
        defaultActiveKey="1"
        items={items}
      />
    </>
  )
}

export default Profile
